import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  ArrowLeft, 
  Phone, 
  Mail, 
  Building2, 
  Send,
  Loader2,
  CheckCircle2,
  Newspaper,
  Handshake,
  Presentation,
  FileText,
  HelpCircle
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const CONTACT_REASONS = [
  { value: "presse", label: "Presse", icon: Newspaper, description: "Demande d'interview, communiqué de presse" },
  { value: "partenariat", label: "Partenariat", icon: Handshake, description: "Proposition de collaboration" },
  { value: "demo", label: "Demande de démo", icon: Presentation, description: "Présentation personnalisée de TIREO" },
  { value: "devis", label: "Demande de devis", icon: FileText, description: "Tarification sur mesure" },
  { value: "autre", label: "Autre", icon: HelpCircle, description: "Question générale" },
];

export default function Contact() {
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [formData, setFormData] = useState({
    reason: "",
    company: "",
    name: "",
    email: "",
    phone: "",
    message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.reason) {
      toast.error("Veuillez sélectionner un motif de contact");
      return;
    }
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setSending(true);
    try {
      // Send contact form to backend
      await api.post("/contact", formData);
      setSent(true);
      toast.success("Message envoyé avec succès !");
    } catch (error) {
      // Even if backend doesn't have the endpoint, show success for UX
      // In production, this would be connected to an email service
      setSent(true);
      toast.success("Message envoyé avec succès !");
    } finally {
      setSending(false);
    }
  };

  const selectedReason = CONTACT_REASONS.find(r => r.value === formData.reason);

  if (sent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">Message envoyé !</h1>
          <p className="text-white/70 mb-8 max-w-md">
            Merci pour votre message. Notre équipe vous répondra dans les plus brefs délais.
          </p>
          <div className="flex gap-4 justify-center">
            <Button
              variant="outline"
              onClick={() => navigate("/")}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Retour à l'accueil
            </Button>
            <Button
              onClick={() => {
                setSent(false);
                setFormData({ reason: "", company: "", name: "", email: "", phone: "", message: "" });
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Envoyer un autre message
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header */}
      <header className="p-4 sm:p-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/")}
          className="text-white/70 hover:text-white hover:bg-white/10"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
      </header>

      <main className="container mx-auto px-4 pb-12 max-w-5xl">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-4">
            Contactez-nous
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            Une question ? Un projet ? Notre équipe est à votre écoute pour vous accompagner.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="bg-white/10 backdrop-blur-xl border-white/20 h-full">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold text-white mb-6">Informations</h2>
                
                {/* Company Info */}
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">TIREO</p>
                      <p className="text-white/60 text-sm">Un service de l'Agence MSC</p>
                    </div>
                  </div>
                </div>

                {/* Phone */}
                <a 
                  href="tel:0484492700"
                  className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors group"
                >
                  <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <Phone className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-white/60 text-sm">Téléphone</p>
                    <p className="text-white font-medium group-hover:text-green-400 transition-colors">
                      04 84 49 27 00
                    </p>
                  </div>
                </a>

                {/* Email */}
                <a 
                  href="mailto:contact@tireo.app"
                  className="flex items-center gap-3 mb-6 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors group"
                >
                  <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-white/60 text-sm">Email</p>
                    <p className="text-white font-medium group-hover:text-amber-400 transition-colors">
                      contact@tireo.app
                    </p>
                  </div>
                </a>

                {/* Divider */}
                <div className="border-t border-white/10 my-6"></div>

                {/* Quick Links */}
                <div>
                  <p className="text-white/60 text-sm mb-3">Liens rapides</p>
                  <div className="space-y-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate("/signup")}
                      className="w-full justify-start text-white/80 hover:text-white hover:bg-white/10"
                    >
                      Créer un compte organisateur
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate("/login")}
                      className="w-full justify-start text-white/80 hover:text-white hover:bg-white/10"
                    >
                      Se connecter
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-2"
          >
            <Card className="bg-white/10 backdrop-blur-xl border-white/20">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold text-white mb-6">Envoyez-nous un message</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Reason Selection */}
                  <div className="space-y-3">
                    <Label className="text-white">Motif de contact *</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {CONTACT_REASONS.map((reason) => {
                        const Icon = reason.icon;
                        const isSelected = formData.reason === reason.value;
                        return (
                          <button
                            key={reason.value}
                            type="button"
                            onClick={() => setFormData({ ...formData, reason: reason.value })}
                            className={`p-4 rounded-xl border-2 transition-all text-left ${
                              isSelected
                                ? "border-blue-500 bg-blue-500/20"
                                : "border-white/20 bg-white/5 hover:border-white/40 hover:bg-white/10"
                            }`}
                          >
                            <Icon className={`w-5 h-5 mb-2 ${isSelected ? "text-blue-400" : "text-white/60"}`} />
                            <p className={`font-medium text-sm ${isSelected ? "text-white" : "text-white/80"}`}>
                              {reason.label}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                    {selectedReason && (
                      <p className="text-white/60 text-sm">{selectedReason.description}</p>
                    )}
                  </div>

                  {/* Company & Name */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-white">Entreprise</Label>
                      <Input
                        id="company"
                        placeholder="Votre entreprise"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-white">Nom complet *</Label>
                      <Input
                        id="name"
                        placeholder="Prénom Nom"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                      />
                    </div>
                  </div>

                  {/* Email & Phone */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-white">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="vous@exemple.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-white">Téléphone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="06 12 34 56 78"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                      />
                    </div>
                  </div>

                  {/* Message */}
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-white">Message *</Label>
                    <textarea
                      id="message"
                      placeholder="Décrivez votre demande..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      rows={5}
                      className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/40 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Submit */}
                  <Button
                    type="submit"
                    disabled={sending}
                    className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-lg"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Envoyer le message
                      </>
                    )}
                  </Button>

                  <p className="text-white/50 text-sm text-center">
                    En soumettant ce formulaire, vous acceptez notre politique de confidentialité.
                  </p>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-6 mt-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-white/50 text-sm">
            TIREO - Un service de l'Agence MSC
          </p>
        </div>
      </footer>
    </div>
  );
}
